// src/store.js
import Vue from 'vue'
import Vuex from 'vuex'
import api from './api'
import dayjs from 'dayjs'
import { makeMessagesModule } from './store/messages'


Vue.use(Vuex)

/* =================== AUTH (API-backed) =================== */
const auth = {
  namespaced: true,
  state: () => ({ user: null, token: null }),
  getters: {
    isAuthenticated: s => !!s.user && !!s.token,
    currentUser: s => s.user,
  },
  mutations: {
    SET_USER (s, u)  { s.user = u },
    SET_TOKEN(s, tk) { s.token = tk },
    CLEAR_AUTH(s)    { s.user = null; s.token = null },
  },
  actions: {
    async login({ commit }, { id, password }) {
      const { data } = await api.post('/auth/login', { id, password })
      const token = data && data.token; const user = data && data.user
      if (!token || !user) throw new Error('Invalid login response')
      localStorage.setItem('edda_token', token)
      api.defaults.headers.Authorization = `Bearer ${token}`
      commit('SET_TOKEN', token); commit('SET_USER', user)
    },
    logout({ commit }) {
      localStorage.removeItem('edda_token'); delete api.defaults.headers.Authorization; commit('CLEAR_AUTH')
    },
    async hydrate({ commit }) {
      const token = localStorage.getItem('edda_token')
      if (!token) { commit('CLEAR_AUTH'); return }
      api.defaults.headers.Authorization = `Bearer ${token}`; commit('SET_TOKEN', token)
      try { const { data } = await api.get('/auth/me'); commit('SET_USER', data) }
      catch { localStorage.removeItem('edda_token'); delete api.defaults.headers.Authorization; commit('CLEAR_AUTH') }
    },
    async me({ commit, state }) {
      if (state.token) api.defaults.headers.Authorization = `Bearer ${state.token}`
      const { data } = await api.get('/auth/me'); commit('SET_USER', data)
    },
    async listUsers(){ const { data } = await api.get('/users'); return data },
    async addUser(_, payload){ await api.post('/users', payload) },
    async toggleActive(_, id){ await api.patch(`/users/${id}/toggle`) },
  }
}

/* =================== SALES (Incentives API) =================== */
const sales = {
  namespaced: true,
  state: () => ({ records: [] }),
  getters: {
    all: s => s.records,
    byId: s => id => s.records.find(r => String(r.id) === String(id)),
    byMonth: s => monthKey => s.records.filter(r => r.dateISO && r.dateISO.startsWith(monthKey)),
    byUserMonth: s => ({ userId, monthKey }) => s.records.filter(r => r.userId === userId && r.dateISO && r.dateISO.startsWith(monthKey)),
    byBranchMonth: s => ({ branchId, monthKey }) => s.records.filter(r => r.branchId === branchId && r.dateISO && r.dateISO.startsWith(monthKey)),
  },
  mutations: {
    SET_ALL(s, rows){ s.records = Array.isArray(rows) ? rows : [] },
    REMOVE(s, id){ s.records = s.records.filter(r => r.id !== id) },
    SET_SALES_FROM_ORDERS(s, list){
      s.records = (Array.isArray(list) ? list : []).map(r => ({
        id: r.id, userId: r.userId, branchId: r.branchId,
        dateISO: r.dateISO, category: r.category,
        listPrice: Number(r.listPrice || 0), saleValue: Number(r.saleValue || 0),
        discountPct: Number(r.discountPct || 0),
      }))
    },
  },
  actions: {
    async fetchMonth({ commit }, monthKey){
      const { data } = await api.get('/sales', { params: { month: monthKey } })
      commit('SET_ALL', data); return data
    },
    async addSale({ dispatch }, payload){
      const body = {
        dateISO: payload.dateISO || dayjs().format('YYYY-MM-DD'),
        category: payload.category, listPrice: Number(payload.listPrice), saleValue: Number(payload.saleValue),
      }
      await api.post('/sales', body); await dispatch('fetchMonth', body.dateISO.slice(0,7))
    },
    async adminUpdateSale({ dispatch }, rec){
      await api.put(`/sales/${rec.id}`, {
        dateISO: rec.dateISO, category: rec.category, listPrice: Number(rec.listPrice), saleValue: Number(rec.saleValue),
        userId: rec.userId, branchId: rec.branchId,
      })
      await dispatch('fetchMonth', rec.dateISO.slice(0,7))
    },
    async adminRemoveSale({ dispatch, getters }, id){
      const row = getters.byId(id); await api.delete(`/sales/${id}`)
      if (row && row.dateISO) await dispatch('fetchMonth', row.dateISO.slice(0,7))
    }
  }
}

/* =================== EXPENSES (API) =================== */
const expenses = {
  namespaced: true,
  state: () => ({ byMonth: {} }),
  getters: { get: s => (branchId, monthKey) => s.byMonth[`${branchId}_${monthKey}`] || 0 },
  mutations: { SET_EXPENSE(s, { branchId, monthKey, amount }){ Vue.set(s.byMonth, `${branchId}_${monthKey}`, Number(amount||0)) } },
  actions: {
    async get({ commit }, { branchId, monthKey }){ const { data } = await api.get(`/expenses/${branchId}/${monthKey}`); commit('SET_EXPENSE', { branchId, monthKey, amount: data.amount }); return data.amount },
    async setExpense({ commit }, { branchId, monthKey, amount }){ await api.put(`/expenses/${branchId}/${monthKey}`, { amount: Number(amount||0) }); commit('SET_EXPENSE', { branchId, monthKey, amount: Number(amount||0) }) }
  }
}

/* =================== CUSTOMERS (API) =================== */
/* =================== CUSTOMERS (shim over LEADS API) =================== */
const customers = {
  namespaced: true,
  state: () => ({ rows: [] }),
  getters: {
    // Only show matured leads (inquiries) to quotation pages
    all: s => (s.rows || []).filter(x => (x.status || '').toLowerCase() === 'matured')
                             .map(x => ({
                               id: x.id,
                               name: x.name,
                               phone: x.phone,
                               email: x.email,
                               address: x.address
                             })),
    byId: (s, g) => id => g.all.find(c => String(c.id) === String(id)),
    search: (s, g) => t => (g.all || []).filter(c => {
      const q = (t || '').toLowerCase()
      return (c.name || '').toLowerCase().includes(q) ||
             (c.phone || '').toLowerCase().includes(q) ||
             (c.email || '').toLowerCase().includes(q)
    }),
  },
  mutations: {
    SET_ALL(s, list){ s.rows = Array.isArray(list) ? list : [] }
  },
  actions: {
    // Pull the authenticated user’s leads; components will read only matured
    async fetch({ commit }) {
      const { data } = await api.get('/leads')
      commit('SET_ALL', Array.isArray(data) ? data : [])
    }
  }
}


/* =================== MODULE CATALOG (API) =================== */
const catalog = {
  namespaced: true,
  state: () => ({ byCat: { kitchen:[], wardrobe:[], vanity:[] } }),
  getters: {
    list: s => cat => s.byCat[cat] || []
  },
  mutations: {
    SET(s, { cat, rows }){ Vue.set(s.byCat, cat, rows) }
  },
  actions: {
    async fetch({ commit }, cat){
      const { data } = await api.get('/modules', { params: { category: cat } })
      commit('SET', { cat, rows: data })
      return data
    }
  }
}

/* =================== QUOTATIONS (API) =================== */
const quotations = {
  namespaced: true,
  state: () => ({ rows: [] }),
  getters: {
    all: s => s.rows,
    byId: s => id => s.rows.find(q => String(q.id) === String(id)),
    byCustomer: s => cid => s.rows.filter(q => String(q.leadId) === String(cid)), // keep alias name
    draftsByCustomer: s => cid => s.rows.filter(q => String(q.leadId) === String(cid) && q.status === 'draft'),
  },
  mutations: {
    SET_ALL(s, list){ s.rows = Array.isArray(list) ? list : [] },
    ADD(s, q){ s.rows.unshift(q) },
    UPDATE(s, q){ const i = s.rows.findIndex(x => x.id === q.id); if (i > -1) s.rows.splice(i,1,q) }
  },
  actions: {
    // NOTE: keep the old call signature but treat "customerId" as leadId
    async fetch({ commit }, { customerId, leadId }) {
      const lid = leadId || customerId
      const { data } = await api.get('/quotations', { params: { leadId: lid } })
      commit('SET_ALL', data); return data
    },
    async createDraft({ commit }, payload) {
      const body = { leadId: payload.leadId || payload.customerId, items: payload.items || [], notes: payload.notes || '' }
      const { data } = await api.post('/quotations', body)
      // refresh list for this lead
      const fresh = await api.get('/quotations', { params: { leadId: body.leadId } })
      commit('SET_ALL', fresh.data)
      return fresh.data.find(x => String(x.id) === String(data.id))
    },
    async save({ commit }, q) {
      await api.put(`/quotations/${q.id}`, { notes: q.notes, items: q.items })
      const { data } = await api.get('/quotations', { params: { leadId: q.leadId || q.customerId } })
      commit('SET_ALL', data)
      return data.find(x => String(x.id) === String(q.id))
    },
    async markSent(_, id){ await api.put(`/quotations/${id}`, { status: 'sent' }) },
    async attachSigned(_, { id, dataUrl, filename }){
      const body = { dataUrl }; if (filename) body.filename = filename
      await api.post(`/quotations/${id}/signed`, body)
    }
  }
}


/* =================== ORDERS (API) =================== */
const orders = {
  namespaced: true,
  state: () => ({ rows: [] }),
  getters: {
    all: s => s.rows,
    byId: s => id => (s.rows || []).find(o => String(o.id) === String(id)),
    byLead: s => lid => (s.rows || []).filter(o => String(o.leadId) === String(lid)),
    // Backwards alias used by existing components
    byCustomer: (s, g) => cid => g.byLead(cid),
    ongoingByCustomer: (s, g) => cid => g.byLead(cid).filter(o => o.stage !== 'CLOSED'),
    pastByCustomer: (s, g) => cid => g.byLead(cid).filter(o => o.stage === 'CLOSED'),
  },
  mutations: {
    SET_ALL(s, list){ s.rows = Array.isArray(list) ? list : [] },
    UPSERT(s, o){
      const i = (s.rows || []).findIndex(x => String(x.id) === String(o.id))
      if (i > -1) s.rows.splice(i, 1, o); else s.rows.unshift(o)
    }
  },
  actions: {
    async listAll({ commit }, params = {}) {
      const { data } = await api.get('/orders', { params })
      commit('SET_ALL', data); return data
    },
    // Keep old name; pass through as leadId
    async listForCustomer({ commit }, customerId){
      const { data } = await api.get('/orders', { params: { leadId: customerId } })
      commit('SET_ALL', data); return data
    },
    async createFromQuotation({ dispatch }, q){
      const { data } = await api.post('/orders', { quotationId: q.id })
      await dispatch('fetchOne', data.id)
      return data
    },
    async fetchOne({ commit }, id){
      const { data } = await api.get(`/orders/${id}`)
      commit('UPSERT', data); return data
    },

    // ---- RM / Purchase / Production (unchanged) ----
    async purchaseFetchMissing(_, orderId){
      const { data } = await api.get(`/orders/${orderId}/rm/missing`)
      return data || []
    },
    async ensureRMList(_, orderId){
      const { data } = await api.get(`/orders/${orderId}/rm-list`)
      if (!data || !data.length){ await api.post(`/orders/${orderId}/rm-list/generate`) }
    },
    async rmMarkMissing({ dispatch }, { orderId, missingItems }){
      await api.post(`/orders/${orderId}/rm/missing`, { items: missingItems })
      return dispatch('fetchOne', orderId)
    },
    async rmIssueAll({ dispatch }, { orderId }){
      await api.post(`/orders/${orderId}/rm/issue`, { all:true })
      return dispatch('fetchOne', orderId)
    },
    async rmConfirmReceived({ dispatch }, { orderId }){
      await api.post(`/orders/${orderId}/rm/received`)
      return dispatch('fetchOne', orderId)
    },
    async purchaseDispatch({ dispatch }, { orderId, items }){
      if (!items || !items.length) {
        const { data } = await api.get(`/orders/${orderId}/rm/missing`)
        items = data || []
      }
      await api.post(`/orders/${orderId}/purchase/dispatch`, { items })
      return dispatch('fetchOne', orderId)
    },
    async prodGenerateCuttingList({ dispatch }, { orderId }){
      await api.post(`/orders/${orderId}/prod/cutting-list/generate`)
      return dispatch('fetchOne', orderId)
    },
    async prodStart({ dispatch }, { orderId }){
      await api.post(`/orders/${orderId}/prod/start`)
      return dispatch('fetchOne', orderId)
    },
    async prodMoveNext({ dispatch }, { orderId, next='QC' }){
      await api.post(`/orders/${orderId}/stage`, { next })
      return dispatch('fetchOne', orderId)
    },
    async listAllByStages({ commit }, stages = []) {
      try {
        if (stages && stages.length) {
          const csv = stages.join(',')
          const { data } = await api.get('/orders', { params: { stage_in: csv } })
          commit('SET_ALL', data); return data
        }
      } catch (_) {}
      const out = [], seen = {}
      const wanted = stages && stages.length ? stages : ['BM_CHECK','DESIGNER_PACK','RM_CHECK','PURCHASE_DPT','PRODUCTION','QC','DISPATCH','INSTALL']
      for (let i=0; i<wanted.length; i++) {
        try {
          const { data } = await api.get('/orders', { params: { stage: wanted[i] } })
          ;(data || []).forEach(o => { if (!seen[o.id]) { seen[o.id]=1; out.push(o) } })
        } catch (e) {}
      }
      commit('SET_ALL', out); return out
    },
    async prodFetchCuttingList(_, orderId){
      const { data } = await api.get(`/orders/${orderId}/prod/cutting-list`)
      return data || []
    },
    async prodEnsureCuttingList(_, orderId){
      const { data } = await api.get(`/orders/${orderId}/prod/cutting-list`)
      if (!data || !data.length){ await api.post(`/orders/${orderId}/prod/cutting-list/generate`) }
      const { data: again } = await api.get(`/orders/${orderId}/prod/cutting-list`)
      return again || []
    },
    async prodComplete({ dispatch }, { orderId }){
      await api.post(`/orders/${orderId}/prod/complete`)
      return dispatch('fetchOne', orderId)
    }
  }
}


/* =================== FOLLOW-UPS (API) =================== */
const followups = {
  namespaced: true,
  state: () => ({ rows: [] }),
  getters: {
    all: s => s.rows,
    overdue: s => (s.rows || []).filter(f => !f.isDone && new Date(f.dueDate) < new Date(new Date().toISOString().slice(0,10))),
    upcoming: s => (s.rows || []).filter(f => !f.isDone && new Date(f.dueDate) >= new Date(new Date().toISOString().slice(0,10))),
    done: s => (s.rows || []).filter(f => !!f.isDone),
  },
  mutations: { SET_ALL(s, list){ s.rows = Array.isArray(list)?list:[] } },
  actions: {
    async fetch({ commit }, params = {}) {
      const { data } = await api.get('/followups', { params })
      commit('SET_ALL', data); return data
    },
    async markDone({ dispatch }, f) {
      // f must have leadId & id (our API returns both)
      await api.patch(`/leads/${f.leadId}/followups/${f.id}`, { isDone: 1 })
      return dispatch('fetch') // refresh default (pending)
    }
  }
}



export default new Vuex.Store({
  modules: { auth, sales, expenses, customers, catalog, quotations, orders,followups,messages: makeMessagesModule() }
})

